import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import Breadcrumb from '../components/breadcrumb';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/auth.service';
import { getJobsiteManagementAccess } from '../config/roleAccess';
import { lookupService, Sector, Industry, FundingRes, Province, Trade as BaseTrade, JobCompStatus } from '../services/lookups.service';
import { Contractor, ContractorService } from '../services/contractor.service';
import { JobsiteService, geocodeAddressIfNeeded } from '../services/jobsite.service';
import { toast } from 'react-toastify';
import { FilterIcon } from 'lucide-react';
import { JobsiteManagementSkeleton } from '../components/Skeleton';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useOfflineDataStatus } from '../hooks/useOfflineDataStatus';
import { getJobsiteCompletionPercentage } from '../utils/jobsiteUtils';

// Import our new components
import JobsiteHeader from '../components/jobsite-management/JobsiteHeader';
import JobsiteMapView from '../components/jobsite-management/JobsiteMapView';
import JobsiteListView from '../components/jobsite-management/JobsiteListView';
import { createJobsiteColumns } from '../components/jobsite-management/JobsiteTableColumns';
import MapService from '../components/jobsite-management/MapService';
import SyncDialogNew from '../components/jobsite-management/SyncDialogNew';
import FilterOverlay from '../components/FilterOverlay';

// Extending the base Trade interface to include category
interface Trade extends BaseTrade {
  category?: string;
}

const JobSiteManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOnline } = useNetworkStatus();
  const { hasOfflineData } = useOfflineDataStatus();

  // Get state from location if coming back from jobsite details
  const { state } = location;
  const focusJobsiteId = state?.jobsiteId;

  // Custom navigation function to handle viewMode reset
  const customNavigate = (to: string, options?: any) => {
    // Reset viewMode to 'list' when navigating away from jobsite management
    // EXCEPT when navigating to jobsite details or if back=true is in options
    const isJobsiteDetailsNavigation = to.includes('/jobsite-management/details/');
    const isAddJobsiteNavigation = to.includes('/jobsite-management/add-jobsite');
    const isBackNavigation = options?.state?.back === true;

    if (!isJobsiteDetailsNavigation && !isBackNavigation && !isAddJobsiteNavigation) {
      // Reset to list view for any navigation except to details pages
      sessionStorage.setItem('jobsiteViewMode', 'list');
    }

    // Proceed with navigation
    navigate(to, options);
  };

  // State for jobsites data and presentation
  const [jobSites, setJobSites] = useState<any[]>([]); // Paginated jobsites for sidebar
  const [allJobsitesForMap, setAllJobsitesForMap] = useState<any[]>([]); // All jobsites for map markers
  const [viewMode, setViewMode] = useState<'map' | 'list'>(() => {
    if (state?.back) {
      return 'list';
    }
    return state?.viewMode || sessionStorage.getItem('jobsiteViewMode') as 'map' | 'list' || 'list';
  });

  // Sync dialog and state
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // UI state
  const [showFilterOverlay, setShowFilterOverlay] = useState(false);
  // const [showAddJobsiteDialog, setShowAddJobsiteDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSidebarLoading, setIsSidebarLoading] = useState(false);
  const [hasVisitedMapView, setHasVisitedMapView] = useState<boolean>(() => {
    if (state?.back) return false;
    return state?.viewMode === 'map' || sessionStorage.getItem('jobsiteViewMode') === 'map';
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 10;

  // Lookup data state
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [fundingResources, setFundingResources] = useState<FundingRes[]>([]);
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [jobCompletionStatuses, setJobCompletionStatuses] = useState<JobCompStatus[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);

  // Selected jobsite state
  const [selectedJobsiteId, setSelectedJobsiteId] = useState<string | null>(null);

  // Create a ref to track previous online status
  const prevOnlineRef = useRef(isOnline);

  // Effect to handle view mode changes and save to session storage
  useEffect(() => {
    sessionStorage.setItem('jobsiteViewMode', viewMode);
    if (viewMode === 'map') setHasVisitedMapView(true);
  }, [viewMode]);

  // Set default viewMode to 'list' when browser reloads
  useEffect(() => {
    // This will run only once when the component mounts
    const handleBeforeUnload = () => {
      // Set viewMode to 'list' when user reloads the page
      sessionStorage.setItem('jobsiteViewMode', 'list');
    };

    // Add event listener for page reload
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup the event listener when component unmounts
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Load saved filters from session storage if needed
  useEffect(() => {
    const defaultFilters = {
      status: [],
      contractor: [],
      mechanicalContractor: [],
      localUnion: [],
      organizer: [],
      sector: [],
      trade: [],
      search: '',
      workersType: [],
      province: []
    };

    // If we have state with preserveFilters flag, make sure we use the saved filters
    if (state?.preserveFilters) {
      // Get saved filters from sessionStorage
      const savedFilters = sessionStorage.getItem('jobsiteFilters');
      if (savedFilters) {
        try {
          const parsed = JSON.parse(savedFilters);
          // Merge with defaults to ensure all fields exist
          setActiveFilters({ ...defaultFilters, ...parsed });
        } catch (error) {
          console.error('Error parsing saved filters:', error);
        }
      }
    } else if (!state) {
      // If there's no state (direct navigation), try to restore filters from sessionStorage
      const savedFilters = sessionStorage.getItem('jobsiteFilters');
      if (savedFilters) {
        try {
          const parsed = JSON.parse(savedFilters);
          // Merge with defaults to ensure all fields exist
          setActiveFilters({ ...defaultFilters, ...parsed });
        } catch (error) {
          console.error('Error parsing saved filters:', error);
        }
      }
    }
    if (state?.back) {
      sessionStorage.setItem('jobsiteViewMode', 'list');
      setViewMode('list');
    }
  }, [state]);

  // Fetch all lookup data in parallel
  useEffect(() => {
    const fetchLookupData = async () => {
      try {
        const user = authService.getUser();

        const [
          sectorData,
          industriesData,
          fundingData,
          provincesData,
          tradesData,
          statusData
        ] = await Promise.all([
          lookupService.getSectors(),
          lookupService.getIndustries(),
          lookupService.getFundingRes(),
          lookupService.getProvinces(),
          lookupService.getTrades(),
          lookupService.getJobCompStatus()
        ]);

        setSectors(sectorData);
        setIndustries(industriesData);
        setFundingResources(fundingData);
        setProvinces(provincesData);
        // Filter to only show UA trades
        const uaTrades = tradesData.filter((t: Trade) => t.category === 'UA');
        setTrades(uaTrades);
        setJobCompletionStatuses(statusData);

        // Fetch mechanical contractors (all active contractors)
        // Use a very high limit to get all contractors
        const contractorsResponse = await ContractorService.getContractorsWithFilters({
          limit: 10000, // Get all contractors
          page: 1
        });
        const filteredContractors = contractorsResponse.data.filter((contractor: any) => {
          // Include contractor if they have at least one address in an available province
          return contractor.address?.length > 0 ? true : false;
        });
        setContractors(filteredContractors);
      } catch (error) {
        console.error('Error fetching lookup data:', error);
        toast.error('Failed to load some reference data. Please refresh the page.');
      }
    };

    fetchLookupData();
  }, []);

  // Transform jobSites data for table display
  const tableData = useMemo(() => {
    return jobSites.map(site => {
      const address = site.addresses;
      let fullAddress = '-';

      if (address) {
        const { address1, address2, city, postalCode, province } = address;

        // Collect non-empty address parts (excluding province for now)
        const coreParts = [address1, address2, city, postalCode].filter(part => part && part.trim() !== '');

        // If there are core address parts, include province (if available)
        if (coreParts.length > 0) {
          if (province?.name) coreParts.push(province.name);
          fullAddress = coreParts.join(', ');
        }
      }

      // Create truncated address if needed
      const displayAddress = fullAddress !== '-' && fullAddress.length > 30
        ? fullAddress.substring(0, 30) + '...'
        : fullAddress;

      return {
        jobsite: {
          id: site.id,
          name: site.jobSiteName
        },
        address: displayAddress,
        originalAddress: fullAddress, // Store the original full address for tooltip
        province: address?.province?.name || '-',
        contractor: site.generalContractor?.name || 'N/A',
        status: site.status, // StatusPill component handles formatting
        completion: `${getJobsiteCompletionPercentage(site.jobCompletionStatus?.status || 'Start')}%`,
        createdBy: site.createdBy?.name || '-' // Use createdBy.name
      };
    });
  }, [jobSites]);

  // Handle page changes
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle jobsite click
  const handleJobsiteClick = (siteId: string) => {
    if (isOnline && siteId?.startsWith('new_')) {
      setShowSyncDialog(true);
    } else {
      setSelectedJobsiteId(siteId);
      customNavigate(`/jobsite-management/details/${siteId}`);
    }
  };

  // Handle jobsite card click
  const handleJobsiteCardClick = (site: any) => {
    // Set the selected jobsite
    setSelectedJobsiteId(site.id);

    // If we're in map view, show the jobsite on the map
    if (viewMode === 'map') {
      showJobsiteOnMap(site);
    } else {
      // Otherwise navigate to details
      if (isJobsiteClickEnable(site)) {
        customNavigate(`/jobsite-management/details/${site.id}`, {
          state: { viewMode }
        });
      } else {
        toast.info("This is a new offline jobsite. Sync to view details online.");
      }
    }
  };

  // Show jobsite on map
  const showJobsiteOnMap = async (site: any) => {
    if (!site.addresses?.coordinates?.coordinates) {
      console.warn(`No coordinates available for jobsite: ${site.jobSiteName}`);
      return;
    }

    try {
      await MapService.centerOnJobsite(site.id);
    } catch (error) {
      console.error('Error centering on jobsite:', error);
      // Fallback: just select the jobsite without centering
      setSelectedJobsiteId(site.id);
    }
  };

  // Handle jobsite name click in card view
  const handleJobsiteNameClick = (site: any) => {
    // Only navigate to details if it's a valid online jobsite
    if (isJobsiteClickEnable(site)) {
      customNavigate(`/jobsite-management/details/${site.id}`, { state: { viewMode } });
    } else {
      toast.info("This is a new offline jobsite. Sync to view details online.");
    }
  };

  // Helper functions for jobsite status
  const isJobsiteClickEnable = (jobsiteDetails: any) => {
    return !(isOnline && jobsiteDetails?.id?.startsWith('new_'));
  };

  const isJobsiteOffline = (jobsiteDetails: any) => {
    return jobsiteDetails?.id?.startsWith('new_');
  };

  // Create table columns
  const columns = useMemo(() => createJobsiteColumns(handleJobsiteClick, isJobsiteOffline), []);

  // Handle view mode changes
  const handleMapViewClick = () => {
    setViewMode('map');
  };

  const handleListViewClick = () => {
    setViewMode('list');
  };

  // Handle sync dialog
  const handleSync = async (status: string, progress: number, message: string) => {
    try {
      setIsSyncing(true);
      // Refresh jobsites data after sync
      await getUsers(currentPage);
      // Close sync dialog after successful sync
      if (status === 'COMPLETED') {
        setShowSyncDialog(false);
        setIsSyncing(false);
      }
    } catch (error) {
      console.error('Error refreshing data after sync:', error);
      setIsSyncing(false);
    }
  };

  // Add state for filters
  const [activeFilters, setActiveFilters] = useState<{
    status: string[];
    contractor: string[];
    mechanicalContractor: string[];
    localUnion: string[];
    organizer: string[];
    sector: string[];
    trade: string[];
    search: string;
    workersType: string[];
    province: string[];
  }>(() => {
    // Default filter values
    const defaultFilters = {
      status: [],
      contractor: [],
      mechanicalContractor: [],
      localUnion: [],
      organizer: [],
      sector: [],
      trade: [],
      search: '',
      workersType: [],
      province: []
    };

    // Try to get saved filters from sessionStorage
    const savedFilters = sessionStorage.getItem('jobsiteFilters');
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        // Merge with defaults to ensure all fields exist
        return {
          ...defaultFilters,
          ...parsed
        };
      } catch (error) {
        console.error('Error parsing saved filters:', error);
        return defaultFilters;
      }
    }
    return defaultFilters;
  });

  // Memoize filtered contractors to avoid unnecessary re-renders
  const filteredContractors = useMemo(() => {
    let filtered = contractors;

    // Filter by mechanical contractor if specified
    if (activeFilters.mechanicalContractor && activeFilters.mechanicalContractor.length > 0) {
      filtered = filtered.filter((contractor: Contractor) =>
        activeFilters.mechanicalContractor.includes(contractor.id)
      );
    }

    // Filter by province if specified
    if (activeFilters.province && activeFilters.province.length > 0) {
      filtered = filtered.filter((contractor: Contractor) => {
        // Include contractor if at least one of their addresses is in one of the selected provinces
        if (!contractor.address || contractor.address.length === 0) {
          return false;
        }
        return contractor.address.some(addr => {
          // addr.province could be a string ID or an object with an id property
          const provinceId = typeof addr.province === 'string'
            ? addr.province
            : (addr.province as any)?.id;
          return provinceId && activeFilters.province.includes(provinceId);
        });
      });
    }

    return filtered;
  }, [contractors, activeFilters.mechanicalContractor, activeFilters.province]);

  // Add state for sorting
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' | null }>({
    key: 'jobsite',
    direction: null
  });

  // Reference to track previous filters
  const prevActiveFiltersRef = useRef(activeFilters);
  // Reference to track initial mount
  const isInitialMount = useRef(true);
  // Reference to track whether data has been fetched
  const hasInitialFetch = useRef(false);

  // Update searchTerm when filters change
  const [searchTerm, setSearchTerm] = useState<string>(activeFilters.search || '');

  // Main function to fetch jobsites
  const getUsers = async (page: number = 1) => {
    const isSort = sortConfig.direction !== null;
    if (!isSort) {
      // Initial load (isLoading still true) or list view → full skeleton
      // Map view after initial load → sidebar spinner only
      if (viewMode === 'map' && !isLoading) {
        setIsSidebarLoading(true);
      } else {
        setIsLoading(true);
      }
    }
    try {
      // Map the 'jobsite' accessor to 'jobSiteName' for the API
      const sortByField = sortConfig.key === 'jobsite' ? 'jobSiteName' : sortConfig.key;

      // Create base params
      const params = {
        status: activeFilters.status,
        contractor: activeFilters.contractor,
        mechanicalContractor: activeFilters.mechanicalContractor,
        localUnion: activeFilters.localUnion,
        organizer: activeFilters.organizer,
        trades: activeFilters.trade,
        sector: activeFilters.sector,
        workersType: activeFilters.workersType,
        province: activeFilters.province,
        search: activeFilters.search,
        page: isOnline ? page.toString() : '1', // Always use page 1 when offline
        limit: isOnline ? pageSize.toString() : '1000', // Use large limit when offline to get all records
      };

      // Only add sort parameters if there's an active sort
      if (sortConfig.direction) {
        Object.assign(params, {
          sortBy: sortByField,
          sortOrder: sortConfig.direction
        });
      }

      const response = await JobsiteService.getJobsites(params);
      
      // Geocode addresses that don't have coordinates (mainly from offline sync)
      const jobsitesWithGeocodedAddresses = await Promise.all(
        response.data.map(async (jobsite: any) => {
          if (jobsite.addresses && isOnline) {
            try {
              const geocodedAddress = await geocodeAddressIfNeeded(jobsite.addresses);
              return {
                ...jobsite,
                addresses: geocodedAddress
              };
            } catch (error) {
              console.warn('Failed to geocode address for jobsite:', jobsite.jobSiteName, error);
              return jobsite; // Return original if geocoding fails
            }
          }
          return jobsite;
        })
      );
      
      setJobSites(jobsitesWithGeocodedAddresses);
      setTotalItems(response.totalCount);

      // Map will be initialized via useEffect when jobSites and viewMode are ready
    } catch (error) {
      console.error('Error fetching jobsites:', error);
      toast.error('Failed to fetch jobsites');
    } finally {
      if (!isSort) {
        setIsLoading(false);
        setIsSidebarLoading(false);
      }
    }
  };

  // Function to fetch ALL jobsites for map markers (unpaginated)
  const getAllJobsitesForMap = async () => {
    try {
      // Create params with same filters but no pagination
      const params = {
        status: activeFilters.status,
        contractor: activeFilters.contractor,
        mechanicalContractor: activeFilters.mechanicalContractor,
        localUnion: activeFilters.localUnion,
        organizer: activeFilters.organizer,
        trades: activeFilters.trade,
        sector: activeFilters.sector,
        workersType: activeFilters.workersType,
        search: activeFilters.search,
        page: '1',
        limit: '10000', // Large limit to get all jobsites
      };

      const response = await JobsiteService.getJobsites(params);

      // Geocode addresses that don't have coordinates
      const jobsitesWithGeocodedAddresses = await Promise.all(
        response.data.map(async (jobsite: any) => {
          if (jobsite.addresses && isOnline) {
            try {
              const geocodedAddress = await geocodeAddressIfNeeded(jobsite.addresses);
              return {
                ...jobsite,
                addresses: geocodedAddress
              };
            } catch (error) {
              console.warn('Failed to geocode address for jobsite:', jobsite.jobSiteName, error);
              return jobsite; // Return original if geocoding fails
            }
          }
          return jobsite;
        })
      );

      setAllJobsitesForMap(jobsitesWithGeocodedAddresses);
    } catch (error) {
      console.error('Error fetching all jobsites for map:', error);
      toast.error('Failed to fetch jobsites for map');
    }
  };

  // useEffect to handle pagination and sorting for paginated jobsite cards
  useEffect(() => {
    // Only log and fetch after initialization
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // Perform initial fetch if not done yet
      if (!hasInitialFetch.current) {
        getUsers(currentPage);
        hasInitialFetch.current = true;
      }
      return;
    }

    // When activeFilters change, reset to page 1
    if (activeFilters !== prevActiveFiltersRef.current) {
      setCurrentPage(1);
      prevActiveFiltersRef.current = activeFilters;
    }

    // Fetch paginated jobsites for the sidebar/cards
    getUsers(currentPage);
  }, [currentPage, sortConfig, activeFilters]);

  // Separate useEffect to handle map markers - only updates when filters change or view switches to map
  useEffect(() => {
    // Only fetch all jobsites for map when in map view and filters change
    if (viewMode === 'map') {
      getAllJobsitesForMap();
    }
  }, [activeFilters, viewMode]);


  // Add debounce function
  const debounce = (func: Function, wait: number) => {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // Create a stable debounced search function
  const debouncedSearchRef = useRef<any>(null);
  
  if (!debouncedSearchRef.current) {
    debouncedSearchRef.current = debounce((value: string) => {
      setActiveFilters((prev: typeof activeFilters) => {
        const newFilters = {
          ...prev,
          search: value
        };
        // Save updated filters to sessionStorage
        sessionStorage.setItem('jobsiteFilters', JSON.stringify(newFilters));
        return newFilters;
      });
    }, 500);
  }

  // Update search handler to trigger debounced search
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    debouncedSearchRef.current(value);
  };

  // Handle filter application
  const handleApplyFilters = (filters: {
    status: string[];
    contractor: string[];
    mechanicalContractor: string[];
    localUnion: string[];
    province: string[];
    organizer: string[];
    sector: string[];
    trade: string[];
    workersType: string[];
    search: string;
  }) => {
    setActiveFilters(filters);
    // Save filters to sessionStorage
    sessionStorage.setItem('jobsiteFilters', JSON.stringify(filters));
    setShowFilterOverlay(false);

    // Map loading will be handled automatically by useEffect when data changes
  };

  // Handle clear filters
  const handleClearFilters = () => {
    const clearedFilters = {
      status: [],
      contractor: [],
      mechanicalContractor: [],
      localUnion: [],
      province: [],
      organizer: [],
      sector: [],
      trade: [],
      search: '',
      workersType: []
    };
    setActiveFilters(clearedFilters);
    // Clear filters from sessionStorage
    sessionStorage.setItem('jobsiteFilters', JSON.stringify(clearedFilters));
    setCurrentPage(1);
    // Also clear the search term input
    setSearchTerm('');
  };

  // Handle sort
  const handleSort = (key: string, direction: 'asc' | 'desc' | null) => {
    setSortConfig({ key, direction });
    // Reset to first page when sorting changes
    setCurrentPage(1);
  };

  // Handle Add Jobsite button
  const handleAddJobsite = () => {
    customNavigate('/jobsite-management/add-jobsite');
    // setShowAddJobsiteDialog(true);
  };

  // Handle sync button click
  const handleSyncClick = () => {
    setShowSyncDialog(true);
  };

  // Count active filters
  const getActiveFiltersCount = () => {
    return (
      activeFilters.status.length +
      activeFilters.contractor.length +
      activeFilters.mechanicalContractor.length +
      activeFilters.localUnion.length +
      activeFilters.organizer.length +
      activeFilters.sector.length +
      activeFilters.trade.length +
      activeFilters.workersType.length +
      activeFilters.province.length
    );
  };

  // Add useEffect to monitor network status changes
  useEffect(() => {
    // If transitioning from offline to online
    if (isOnline && !prevOnlineRef.current) {
      console.log('Back online, refreshing jobsites data...');
      // Reset to first page when coming back online
      setCurrentPage(1);
      // Refresh jobsites data
      getUsers();
      // Show sync dialog if there's offline data
      if (hasOfflineData) {
        setShowSyncDialog(true);
      }
    }
    // If transitioning from online to offline  
    else if (!isOnline && prevOnlineRef.current) {
      console.log('Going offline, resetting to page 1...');
      // Reset to first page when going offline
      setCurrentPage(1);
      // Fetch all offline data
      getUsers(1);
    }

    // Update the ref with current status
    prevOnlineRef.current = isOnline;
  }, [isOnline, hasOfflineData]);

  // Effect to refresh jobsites when event is triggered
  useEffect(() => {
    const handleRefreshJobsites = () => {
      getUsers();
    };
    window.addEventListener('REFRESH_JOBSITES', handleRefreshJobsites);
    return () => {
      window.removeEventListener('REFRESH_JOBSITES', handleRefreshJobsites);
    };
  }, []);

  // Get user role and access permissions
  const user = authService.getUser();
  const jobsiteAccess = getJobsiteManagementAccess(user?.role || '');

  return (
    <div className="bg-gray-50 p-3 sm:p-4 md:p-6">
      <div className="flex justify-between mb-4 sm:mb-6">
        <Breadcrumb title="Jobsite Management" />

        <div className="flex sm:hidden">
          <button
            id="add-jobsite-btn"
            className="btn-primary text-[24px] sm:text-[16px] rounded-full sm:text-body w-fit sm:w-auto sm:rounded-[8px] px-3 py-1.5 mr-1"
            onClick={handleAddJobsite}
          >
            <span> + </span>
          </button>
          <button
            onClick={() => setShowFilterOverlay(true)}
            className={`relative btn-secondary flex items-center justify-center mr-1 text-[24px] sm:text-[16px] rounded-full sm:text-body w-fit sm:w-auto sm:rounded-[8px] ${
              getActiveFiltersCount() > 0 ? 'bg-[#A42823] text-white hover:bg-[#8a2320]' : ''
            }`}
            id="filter-button"
          >
            <FilterIcon className="w-4 h-4" />
            {getActiveFiltersCount() > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#E6D69B] text-[#000000] text-[10px] rounded-full w-5 h-5 flex items-center justify-center">
                {getActiveFiltersCount()}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Header with search, filters, view toggles */}
      <JobsiteHeader
        viewMode={viewMode}
        searchTerm={searchTerm}
        isOnline={isOnline}
        hasOfflineData={hasOfflineData}
        isSyncing={isSyncing}
        getActiveFiltersCount={getActiveFiltersCount}
        handleSearch={handleSearch}
        handleMapViewClick={handleMapViewClick}
        handleListViewClick={handleListViewClick}
        handleSyncClick={handleSyncClick}
        setShowFilterOverlay={setShowFilterOverlay}
        handleAddJobsite={handleAddJobsite}
        hasAddAccess={jobsiteAccess.addJobsite}
      />

      {isLoading ? (
        <JobsiteManagementSkeleton />
      ) : (
        <div className='bg-white rounded-[12px] shadow'>
          <div className={viewMode === 'list' ? '' : 'hidden'}>
            <div className='p-3 sm:p-4'>
              <JobsiteListView
                tableData={tableData}
                columns={columns}
                totalItems={totalItems}
                currentPage={currentPage}
                pageSize={pageSize}
                sortConfig={sortConfig}
                onPageChange={handlePageChange}
                onSort={handleSort}
                onRowClick={(item) => handleJobsiteClick(item.jobsite.id)}
              />
            </div>
          </div>
          {hasVisitedMapView && (
            <div className={viewMode === 'map' ? '' : 'hidden'}>
              <JobsiteMapView
                isOnline={isOnline}
                isSidebarLoading={isSidebarLoading}
                jobSites={jobSites}
                selectedJobsiteId={selectedJobsiteId}
                currentPage={currentPage}
                pageSize={pageSize}
                totalItems={totalItems}
                getCompletionPercentage={getJobsiteCompletionPercentage}
                handleJobsiteNameClick={handleJobsiteNameClick}
                handleJobsiteCardClick={handleJobsiteCardClick}
                handlePageChange={handlePageChange}
                allJobsitesForMap={allJobsitesForMap}
                mechanicalContractors={filteredContractors}
                setSelectedJobsiteId={setSelectedJobsiteId}
                focusJobsiteId={focusJobsiteId || null}
              />
            </div>
          )}
        </div>
      )}

      {/* Reuse existing dialogs and overlays */}
      <FilterOverlay
        isOpen={showFilterOverlay}
        onClose={() => setShowFilterOverlay(false)}
        onClearAll={handleClearFilters}
        onApply={handleApplyFilters}
        activeFilters={activeFilters}
      />

      <SyncDialogNew
        isOpen={showSyncDialog && isOnline && hasOfflineData}
        onClose={() => setShowSyncDialog(false)}
        handleSync={handleSync}
        getUsers={getUsers}
        isOnline={isOnline}
        jobsiteId={undefined}
      />
    </div>
  );
};

export default JobSiteManagement; 